import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { toast } from '../../stores/uiStore';
import {
  listComments, createComment, updateComment, deleteComment,
  type ProjectComment,
} from '../../api/comments';
import styles from './CommentsPanel.module.css';

function relativeTime(iso: string, t: (key: string) => string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t('comments:justNow');
  if (mins < 60) return t('comments:minutesAgo').replace('{{count}}', String(mins));
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('comments:hoursAgo').replace('{{count}}', String(hours));
  const days = Math.floor(hours / 24);
  return t('comments:daysAgo').replace('{{count}}', String(days));
}

interface Props {
  projectId: string;
}

export function CommentsPanel({ projectId }: Props) {
  const { t } = useTranslation(['comments']);
  const currentUser = useAuthStore((s) => s.user);

  const [comments, setComments] = useState<ProjectComment[]>([]);
  const [newText, setNewText] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  const loadComments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listComments(projectId);
      setComments(data);
    } catch (err) {
      console.error('Failed to load comments:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!newText.trim() || submitting) return;
    setSubmitting(true);
    try {
      const comment = await createComment(projectId, newText.trim());
      setComments((prev) => [comment, ...prev]);
      setNewText('');
    } catch (err) {
      console.error('Failed to post comment:', err);
      toast.error(t('comments:postError', 'Failed to post comment'));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUpdate(commentId: string) {
    if (!editText.trim() || updating) return;
    setUpdating(true);
    try {
      const updated = await updateComment(commentId, editText.trim());
      setComments((prev) => prev.map((c) => (c.id === commentId ? updated : c)));
      setEditingId(null);
      setEditText('');
    } catch (err) {
      console.error('Failed to update comment:', err);
      toast.error(t('comments:updateError', 'Failed to update comment'));
    } finally {
      setUpdating(false);
    }
  }

  async function handleDelete(commentId: string) {
    if (!confirm(t('comments:confirmDelete'))) return;
    try {
      await deleteComment(commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (err) {
      console.error('Failed to delete comment:', err);
      toast.error(t('comments:deleteError', 'Failed to delete comment'));
    }
  }

  const isOwn = (comment: ProjectComment) => currentUser?.id === comment.user_id;
  const isAdmin = currentUser?.role === 'tenant_admin' || currentUser?.role === 'super_admin';

  return (
    <div className={styles.panel}>
      <div className={styles.panelTitle}>{t('comments:title')}</div>

      <form className={styles.form} onSubmit={handleSubmit}>
        <textarea
          className={styles.textarea}
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          placeholder={t('comments:placeholder')}
          rows={2}
        />
        <button
          type="submit"
          className={styles.submitBtn}
          disabled={!newText.trim() || submitting}
        >
          {t('comments:post')}
        </button>
      </form>

      {loading && !comments.length ? (
        <div className={styles.empty}>...</div>
      ) : comments.length === 0 ? (
        <div className={styles.empty}>{t('comments:noComments')}</div>
      ) : (
        comments.map((c) => (
          <div key={c.id} className={styles.comment}>
            <div className={styles.commentHeader}>
              <span className={styles.userName}>{c.user_name || c.user_id.slice(0, 8)}</span>
              <span className={styles.timestamp}>{relativeTime(c.created_at, t)}</span>
            </div>
            {editingId === c.id ? (
              <div>
                <textarea
                  className={styles.textarea}
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  rows={2}
                />
                <div className={styles.commentActions}>
                  <button
                    className={styles.actionBtn}
                    onClick={() => handleUpdate(c.id)}
                    disabled={updating || !editText.trim()}
                  >{t('comments:save', 'Save')}</button>
                  <button className={styles.actionBtn} onClick={() => setEditingId(null)}>
                    {t('comments:cancel', 'Cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className={styles.commentContent}>{c.content}</div>
                {(isOwn(c) || isAdmin) && (
                  <div className={styles.commentActions}>
                    {isOwn(c) && (
                      <button
                        className={styles.actionBtn}
                        onClick={() => { setEditingId(c.id); setEditText(c.content); }}
                      >{t('comments:edit', 'Edit')}</button>
                    )}
                    <button className={styles.deleteBtn} onClick={() => handleDelete(c.id)}>
                      {t('comments:delete', 'Delete')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        ))
      )}
    </div>
  );
}
